import { Analytics } from '@cobliteam/analytics';
import useVehicleGroup from '@cobliteam/hooks/src/useEntity/useVehicleGroup';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import Tag from '@cobliteam/ui/components/Tag';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useForm } from '@cobliteam/ui/form';
import Input from '@cobliteam/ui/form/Input';
import Icon from '@cobliteam/ui/icons';
import { DialogSetupGuideShell } from '@cobliteam/ui-business/components/dialogs/DialogSetupGuideShell/DialogSetupGuideShell';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { z } from 'zod';
import type { SetupGuideEntityFormProps } from '../entityModalConfig';
import SetupGuideFormRow from '../SetupGuideFormRow';

/** Minimal "create a vehicle group" modal — just a name; vehicles/users can be added later on the groups page. */
const GroupsForm = ({ fleetId, item, onClose, onSuccess, onAdvancedSettings }: SetupGuideEntityFormProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { createVehicleGroup, useVehicleGroups } = useVehicleGroup();
  const groupsQuery = useVehicleGroups();

  // Same duplicate rule as the groups page CreateDrawer: case-insensitive name match.
  const existingNames = useMemo(() => new Set((groupsQuery.data ?? []).map((group) => group.name.trim().toLowerCase())), [groupsQuery.data]);
  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t('entity_modal.groups.validation.name_required'))
          .refine((name) => !existingNames.has(name.toLowerCase()), t('entity_modal.groups.validation.name_duplicated')),
      }),
    [existingNames, t]
  );

  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), mode: 'onChange', defaultValues: { name: '' } });
  const { mutateAsync: createGroup, isPending } = createVehicleGroup;

  const handleSubmit = async ({ name }: z.infer<typeof schema>) => {
    try {
      await createGroup({ name: name.trim(), vehicleIds: [], userIds: [] });
    } catch {
      // The hook has no feedback of its own (unlike the other entity hooks) — surface both outcomes here.
      Alert.error(t('entity_modal.groups.created_error'));
      return;
    }
    Alert.success(t('entity_modal.groups.created_toast'));
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'groups', count: 1 });
    onSuccess(1);
  };

  return (
    <DialogSetupGuideShell
      open
      onClose={onClose}
      title={t('entity_modal.groups.title')}
      description={t('entity_modal.groups.description')}
      secondaryAction={{ label: t('entity_modal.advanced_settings'), onClick: onAdvancedSettings }}
      primaryAction={{
        label: t('entity_modal.groups.submit'),
        onClick: form.handleSubmit(handleSubmit),
        disabled: !form.formState.isValid || isPending,
        loading: isPending,
        endIcon: <Icon name='check' size='sm' />,
        'data-testid': 'setup-guide-groups-submit',
      }}
    >
      <Form form={form} onSubmit={handleSubmit}>
        <div className='flex flex-col'>
          <Tag color='default' size='sm' label={t('entity_modal.groups.progress', { current: item.current, target: item.target })} />
          <SetupGuideFormRow label={t('entity_modal.groups.row_label', { index: 1 })}>
            <FormField control={form.control} name='name'>
              {({ field }) => (
                <FormItem className='w-64'>
                  <FormLabel>{t('entity_modal.groups.name_label')}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('entity_modal.groups.name_placeholder')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            </FormField>
          </SetupGuideFormRow>
        </div>
      </Form>
    </DialogSetupGuideShell>
  );
};

export default GroupsForm;
